import "./css/StaticPages.css";
import logo from "../images/svg/sidebar-logo.svg";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">

      <div className="static-page-wrapper">

        <div className="static-page-logo">
          <img src={logo} alt="DidYouSign" />
        </div>

        <div className="text-center static-page-header">
          <h1 className="static-page-title">
            Privacy Policy
          </h1>

          <p className="static-page-effective-date">
            Effective Date: August 20, 2026
          </p>
        </div>

        <div className="static-page-document static-page-content">

          <section>
            <p>
              DidYouSign (“DidYouSign,” “we,” “us,” or “our”) respects your
              privacy. This Privacy Policy explains how we collect, use,
              disclose, and protect information when you use our website,
              applications, electronic signature tools, waiver services,
              messaging features, and related services (collectively, the
              “Service”).
            </p>

            <p className="mt-4">
              By using DidYouSign, you acknowledge the practices described in
              this Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              1. Information We Collect
            </h2>

            <p>
              Depending on how you use DidYouSign, we may collect the following
              information:
            </p>

            <h3 className="static-page-subheading">Account Information</h3>

            <p>
              When you create or manage an account, we may collect information
              such as:
            </p>

            <ul className="static-page-list">
              <li>Name</li>
              <li>Email address</li>
              <li>Mobile phone number</li>
              <li>Company or organization name</li>
              <li>Account credentials</li>
              <li>Billing and subscription information</li>
            </ul>

            <h3 className="static-page-subheading">
              Document and Signature Information
            </h3>

            <p>
              When documents or waivers are created, sent, accessed, or signed
              through DidYouSign, we may process information such as:
            </p>

            <ul className="static-page-list">
              <li>Names and contact information of document recipients</li>
              <li>
                Documents, waivers, agreements, and information contained
                within them
              </li>
              <li>Electronic signatures</li>
              <li>Dates and times associated with document activity</li>
              <li>
                Transaction and audit information associated with signing
                activity
              </li>
            </ul>

            <h3 className="static-page-subheading">
              Information From Document Recipients
            </h3>

            <p>
              If someone sends you a document through DidYouSign, we may
              collect information you provide while accessing or completing
              that document, including your name, contact information,
              responses, and electronic signature.
            </p>

            <h3 className="static-page-subheading">
              Device and Usage Information
            </h3>

            <p>
              We may automatically collect certain technical information when
              you interact with the Service, including:
            </p>

            <ul className="static-page-list">
              <li>IP address</li>
              <li>Browser and device information</li>
              <li>Operating system</li>
              <li>Pages or features accessed</li>
              <li>Dates and times of activity</li>
              <li>Referring URLs</li>
              <li>Diagnostic and security information</li>
            </ul>

            <h3 className="static-page-subheading">Payment Information</h3>

            <p>
              Payments may be processed by third-party payment processors.
              DidYouSign may receive information about the transaction, such
              as payment status, subscription type, billing address, or the
              last digits of a payment method, but we do not necessarily
              receive or store complete payment card information.
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              2. How We Use Information
            </h2>

            <p>We may use information to:</p>

            <ul className="static-page-list">
              <li>Provide and operate the DidYouSign Service.</li>
              <li>Create and manage user accounts.</li>
              <li>
                Send, access, complete, and store electronic documents and
                waivers.
              </li>
              <li>Authenticate users and document recipients.</li>
              <li>
                Maintain records associated with electronic signatures and
                transactions.
              </li>
              <li>
                Send transactional emails and text messages requested by
                users or recipients.
              </li>
              <li>Process subscriptions and payments.</li>
              <li>Provide customer support.</li>
              <li>
                Detect fraud, abuse, security threats, and unauthorized
                activity.
              </li>
              <li>
                Troubleshoot and improve the reliability and functionality of
                the Service.
              </li>
              <li>Comply with applicable legal obligations.</li>
              <li>Enforce our Terms of Service.</li>
            </ul>
          </section>

          <section>
            <h2 className="static-page-heading">
              3. SMS and Mobile Information
            </h2>

            <p>
              DidYouSign may send transactional SMS messages when an individual
              provides a mobile number and requests or consents to receive
              communications related to a document, waiver, signature request,
              or other DidYouSign transaction.
            </p>

            <p>
              Messages may include secure document links, reminders,
              confirmations, or other information directly related to the
              requested transaction.
            </p>

            <p>
              Mobile information, text messaging originator opt-in data, and
              SMS consent will not be sold, rented, or shared with third
              parties or affiliates for marketing or promotional purposes.
            </p>

            <p>
              We may use service providers that process mobile information
              solely as necessary to provide messaging functionality, deliver
              requested communications, prevent fraud or abuse, or operate the
              Service. Such processing does not permit those providers to use
              SMS consent information for their own marketing or promotional
              purposes.
            </p>

            <p>
              Message frequency varies based on user activity and requests.
              Message and data rates may apply.
            </p>

            <p>
              Recipients may reply STOP to opt out of SMS messages or HELP for
              assistance.
            </p>

            <p>
              DidYouSign does not use consent provided for transactional SMS
              messages as consent to receive unrelated marketing or
              promotional messages.
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              4. How We Share Information
            </h2>

            <p>
              We do not sell personal information in exchange for monetary
              compensation.
            </p>

            <p>We may disclose information in limited circumstances, including:</p>

            <h3 className="static-page-subheading">Service Providers</h3>

            <p>
              We may use third-party companies that help us operate DidYouSign,
              such as providers of:
            </p>

            <ul className="static-page-list">
              <li>Cloud hosting and infrastructure</li>
              <li>SMS and email delivery</li>
              <li>Payment processing</li>
              <li>Analytics</li>
              <li>Security and fraud prevention</li>
              <li>Customer support</li>
              <li>Technical operations</li>
            </ul>

            <p>
              These providers may process information only as necessary to
              provide services to us and subject to applicable contractual and
              legal obligations.
            </p>

            <h3 className="static-page-subheading">
              At the Direction of Our Users
            </h3>

            <p>
              DidYouSign facilitates transactions between document senders and
              recipients. Information submitted in connection with a document
              may therefore be accessible to the person or organization that
              created, sent, or manages that document.
            </p>

            <h3 className="static-page-subheading">
              Legal and Safety Requirements
            </h3>

            <p>
              We may disclose information when reasonably necessary to:
            </p>

            <ul className="static-page-list">
              <li>
                Comply with applicable law, regulation, legal process, or
                governmental request.
              </li>
              <li>
                Protect the rights, property, or safety of DidYouSign, our
                users, or others.
              </li>
              <li>
                Investigate fraud, abuse, security incidents, or violations
                of our Terms.
              </li>
            </ul>

            <h3 className="static-page-subheading">Business Transactions</h3>

            <p>
              If DidYouSign is involved in a merger, acquisition, financing,
              restructuring, sale of assets, or similar business transaction,
              information may be transferred as part of that transaction
              subject to applicable law.
            </p>

            <p>
              For clarity, mobile information and SMS opt-in consent will not
              be shared with third parties or affiliates for their marketing
              or promotional purposes, including in connection with lead
              generation.
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              5. Cookies and Similar Technologies
            </h2>

            <p>
              DidYouSign may use cookies and similar technologies to operate the
              Service, remember preferences, maintain sessions, understand how
              features are used, improve performance, and protect against
              fraud and unauthorized access.
            </p>

            <p>
              Your browser may allow you to limit or disable certain cookies.
              Some features of the Service may not function properly if
              necessary cookies are disabled.
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              6. Data Retention
            </h2>

            <p>
              We retain personal information for as long as reasonably
              necessary to provide the Service, maintain transaction and
              document records, comply with legal obligations, resolve
              disputes, prevent fraud, and enforce our agreements.
            </p>

            <p>
              Retention periods may vary depending on the type of information,
              how the Service is used, contractual requirements, and applicable
              law.
            </p>

            <p>
              Users may also have control over certain documents and records
              associated with their accounts.
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              7. Data Security
            </h2>

            <p>
              We use reasonable administrative, technical, and organizational
              safeguards designed to protect information against unauthorized
              access, loss, misuse, alteration, or disclosure.
            </p>

            <p>
              However, no electronic transmission, storage system, or online
              service can be guaranteed to be completely secure.
            </p>

            <p>
              You are responsible for protecting your account credentials and
              should notify us promptly if you believe your account has been
              accessed without authorization.
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              8. Your Privacy Choices
            </h2>

            <p>
              Depending on where you live and applicable law, you may have
              rights regarding your personal information, which may include
              the ability to request:
            </p>

            <ul className="static-page-list">
              <li>
                Access to personal information we maintain about you.
              </li>
              <li>Correction of inaccurate information.</li>
              <li>Deletion of certain information.</li>
              <li>
                Information regarding how your information is used or
                disclosed.
              </li>
            </ul>

            <p>
              Certain information may need to be retained where required for
              legal, security, fraud-prevention, contractual, or
              transaction-record purposes.
            </p>

            <p>
              To submit a privacy request, contact us at{" "}
              <a
                href="mailto:didyousigntx@gmail.com"
                className="text-black font-medium underline"
              >
                didyousigntx@gmail.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              9. SMS Choices
            </h2>

            <p>
              Receiving transactional SMS communications is based on the
              recipient's request or consent.
            </p>

            <p>
              You may opt out of SMS communications at any time by replying
              STOP.
            </p>

            <p>
              For assistance, reply HELP or contact us at{" "}
              <a
                href="mailto:didyousigntx@gmail.com"
                className="text-black font-medium underline"
              >
                didyousigntx@gmail.com
              </a>
              .
            </p>

            <p>
              Opting out of SMS does not prevent you from using other available
              methods to access DidYouSign or communicate with a document
              sender where applicable.
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              10. Third-Party Links and Services
            </h2>

            <p>
              The Service may contain links to or integrate with websites,
              applications, or services operated by third parties.
            </p>

            <p>
              Their privacy practices are governed by their own policies.
              DidYouSign is not responsible for the privacy practices of
              third-party websites or services that we do not control.
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              11. Children's Privacy
            </h2>

            <p>
              DidYouSign is not directed toward children under 13, and we do
              not knowingly collect personal information directly from
              children under 13 without appropriate authorization or as
              otherwise permitted by law.
            </p>

            <p>
              If you believe information from a child under 13 has been
              collected improperly, contact us so that we can review the
              situation.
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              12. Changes to This Privacy Policy
            </h2>

            <p>
              We may update this Privacy Policy periodically to reflect changes
              to the Service, our practices, or applicable requirements.
            </p>

            <p>
              When we make changes, we will update the Effective Date at the
              top of this policy. Additional notice may be provided when
              required by law.
            </p>
          </section>

          <section>
            <h2 className="static-page-heading">
              13. Contact Us
            </h2>

            <p>
              If you have questions about this Privacy Policy or DidYouSign's
              privacy practices, contact:
            </p>

            <p className="mt-4">
              <strong>DidYouSign</strong>
              <br />
              <a
                href="mailto:didyousigntx@gmail.com"
                className="text-black font-medium underline"
              >
                didyousigntx@gmail.com
              </a>
            </p>
          </section>

        </div>

        <footer className="static-page-footer">
          © {new Date().getFullYear()} DidYouSign. All rights reserved.
        </footer>

      </div>
    </div>
  );
}